import React, { useState } from "react";
import { Eye, EyeOff, X } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-hot-toast";

import { postApi } from "../../api/api_instance";
import { API_URLS } from "../../api/apiUrls";

const ResetPasswordToken = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";

  const [formData, setFormData] = useState({
    newPassword: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const tokenError = token
    ? ""
    : "Reset token is missing. Please use the link from your email.";

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const nextErrors = {};

    if (!token) {
      nextErrors.token = tokenError;
    }

    if (!formData.newPassword.trim()) {
      nextErrors.newPassword = "New password is required";
    } else if (formData.newPassword.length < 8) {
      nextErrors.newPassword = "Password must be at least 8 characters";
    }

    if (!formData.confirmPassword.trim()) {
      nextErrors.confirmPassword = "Please confirm your password";
    } else if (formData.newPassword !== formData.confirmPassword) {
      nextErrors.confirmPassword = "Passwords do not match";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);

    try {
      const response = await postApi({
        apiUrl: API_URLS.CHANGE_PASSWORD_WITH_TOKEN,
        data: {
          token,
          newPassword: formData.newPassword,
        },
      });

      if (response?.success === false) {
        toast.error(response?.message || "Failed to update password");
        return;
      }

      toast.success(
        response?.message || "Password updated successfully. Please login.",
      );
      navigate("/organization/login", { replace: true });
    } catch (error) {
      toast.error(error?.message || "Failed to update password");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f5f7f6] flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-gray-100 p-6 sm:p-8 relative">
        <button
          type="button"
          onClick={() => navigate("/organization/login")}
          className="absolute right-4 top-4 text-gray-500 hover:text-gray-700"
          aria-label="Go back to login"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="text-center mb-6">
          <h1 className="text-3xl font-bold text-[#06513D]">CARBURN</h1>
          <p className="text-sm text-gray-600 mt-1">
            Carbon Footprint & ESG Reporting
          </p>
          <h2 className="text-2xl font-semibold text-[#06513D] mt-6">
            Reset Password
          </h2>
          <p className="text-sm text-gray-600 mt-2">
            Create your new password below and save it securely.
          </p>
        </div>

        {(errors.token || tokenError) && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {errors.token || tokenError}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label
              htmlFor="newPassword"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              New Password
            </label>
            <div className="relative">
              <input
                id="newPassword"
                name="newPassword"
                type={showNewPassword ? "text" : "password"}
                placeholder="Enter new password"
                value={formData.newPassword}
                onChange={handleChange}
                className={`block w-full rounded-lg border px-4 py-3 pr-12 text-sm outline-none transition-colors ${
                  errors.newPassword
                    ? "border-red-300 focus:border-red-500"
                    : "border-gray-300 focus:border-[#06513D]"
                }`}
                disabled={isLoading}
              />
              <button
                type="button"
                onClick={() => setShowNewPassword((prev) => !prev)}
                className="absolute inset-y-0 right-0 flex items-center px-4 text-gray-500"
                aria-label="Toggle new password visibility"
              >
                {showNewPassword ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </button>
            </div>
            {errors.newPassword && (
              <p className="mt-1 text-sm text-red-600">{errors.newPassword}</p>
            )}
          </div>

          <div>
            <label
              htmlFor="confirmPassword"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Confirm Password
            </label>
            <div className="relative">
              <input
                id="confirmPassword"
                name="confirmPassword"
                type={showConfirmPassword ? "text" : "password"}
                placeholder="Confirm new password"
                value={formData.confirmPassword}
                onChange={handleChange}
                className={`block w-full rounded-lg border px-4 py-3 pr-12 text-sm outline-none transition-colors ${
                  errors.confirmPassword
                    ? "border-red-300 focus:border-red-500"
                    : "border-gray-300 focus:border-[#06513D]"
                }`}
                disabled={isLoading}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword((prev) => !prev)}
                className="absolute inset-y-0 right-0 flex items-center px-4 text-gray-500"
                aria-label="Toggle confirm password visibility"
              >
                {showConfirmPassword ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </button>
            </div>
            {errors.confirmPassword && (
              <p className="mt-1 text-sm text-red-600">
                {errors.confirmPassword}
              </p>
            )}
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full rounded-lg bg-[#06513D] px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#054634] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isLoading ? "Saving..." : "Save"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ResetPasswordToken;
